import { useState } from 'react';
import { cn } from '../../lib/utils';
import Button from '../ui/Button';
import { confirmHandover } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import { getConfirmationDeadline } from '../../hooks/useHandover';

/**
 * HandoverStatus - Shows the confirmation status for winner and seller
 *
 * Displays who has confirmed and provides the action button for the current user.
 */
export default function HandoverStatus({
  handover,
  isWinner,
  isSeller,
  raffleId,
  onConfirmed,
  className,
}) {
  const [confirming, setConfirming] = useState(false);
  const { toast } = useToast();

  const { winnerConfirmed, sellerConfirmed, status } = handover || {};
  const bothConfirmed = winnerConfirmed && sellerConfirmed;

  // Determine if current user has already confirmed
  const userHasConfirmed = (isWinner && winnerConfirmed) || (isSeller && sellerConfirmed);
  const otherPartyConfirmed = (isWinner && sellerConfirmed) || (isSeller && winnerConfirmed);

  // Handle confirmation
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const role = isWinner ? 'winner' : 'seller';
      const result = await confirmHandover(raffleId, role);

      if (result.success) {
        toast.success(result.bothConfirmed
          ? 'Collection confirmed! The seller will receive their payout shortly.'
          : `Thanks for confirming! We're waiting for the ${isWinner ? 'seller' : 'winner'} to confirm too.`
        );
        onConfirmed?.();
      }
    } catch (error) {
      toast.error(error);
    } finally {
      setConfirming(false);
    }
  };

  // Completed state - both parties confirmed
  if (status === 'completed' || bothConfirmed) {
    return (
      <div className={cn('bg-success/10 border border-success/30 rounded-xl p-6', className)}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
            <CheckIcon className="w-6 h-6 text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-success">Collection Complete!</h3>
            <p className="text-sm text-cream/60">Both parties have confirmed the item handover</p>
          </div>
        </div>
        {isSeller && (
          <p className="text-sm text-cream/70">
            Great news! Your payout is being processed and will arrive in your bank account within 1-2 business days.
          </p>
        )}
        {isWinner && (
          <p className="text-sm text-cream/70">
            Enjoy your prize! The seller has been notified and their payout is being processed.
          </p>
        )}
      </div>
    );
  }

  // Disputed state
  if (status === 'disputed') {
    return (
      <div className={cn('bg-error/10 border border-error/30 rounded-xl p-6', className)}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-error/20 flex items-center justify-center">
            <AlertIcon className="w-6 h-6 text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-error">Issue Reported</h3>
            <p className="text-sm text-cream/60">This handover is under review</p>
          </div>
        </div>
        <p className="text-sm text-cream/70">
          A problem has been reported with this handover. Our support team is reviewing the case and will contact both parties within 24-48 hours.
        </p>
      </div>
    );
  }

  // Pending state - show confirmation UI
  return (
    <div className={cn('bg-midnight-light border border-cream/10 rounded-xl p-6', className)}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
          <HandshakeIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-cream">Confirm Item Collection</h3>
          <p className="text-sm text-cream/60">
            {isWinner ? 'Arrange pickup with the seller' : 'Waiting for the winner to collect'}
          </p>
        </div>
      </div>

      {/* Explanation for winner */}
      {isWinner && !userHasConfirmed && (
        <div className="bg-midnight border border-cream/5 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-cream mb-2">What happens next?</h4>
          <ol className="text-sm text-cream/60 space-y-2">
            <li className="flex gap-2">
              <span className="text-primary font-medium">1.</span>
              Contact the seller using the pickup address above to arrange a time
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-medium">2.</span>
              Meet at the location and collect your prize
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-medium">3.</span>
              Once you have the item, confirm collection below
            </li>
          </ol>
        </div>
      )}

      {/* Explanation for seller */}
      {isSeller && !userHasConfirmed && (
        <div className="bg-midnight border border-cream/5 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-cream mb-2">What happens next?</h4>
          <ol className="text-sm text-cream/60 space-y-2">
            <li className="flex gap-2">
              <span className="text-primary font-medium">1.</span>
              The winner can see your pickup address and will contact you
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-medium">2.</span>
              Arrange a convenient time to hand over the item
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-medium">3.</span>
              Once handed over, confirm below to receive your payout
            </li>
          </ol>
        </div>
      )}

      {/* Status checklist */}
      <div className="space-y-3 mb-6">
        <StatusItem
          label={isWinner ? "I've collected the item" : "Winner confirmed collection"}
          checked={winnerConfirmed}
          isCurrentUser={isWinner}
        />
        <StatusItem
          label={isSeller ? "I've handed over the item" : "Seller confirmed handover"}
          checked={sellerConfirmed}
          isCurrentUser={isSeller}
        />
      </div>

      {/* Action area */}
      {!userHasConfirmed ? (
        <div className="space-y-3">
          <Button
            onClick={handleConfirm}
            loading={confirming}
            fullWidth
            size="lg"
          >
            {isWinner ? "Yes, I've Collected the Item" : "Yes, I've Handed Over the Item"}
          </Button>
          <p className="text-xs text-cream/40 text-center">
            Only confirm once you have physically {isWinner ? 'received' : 'handed over'} the item
          </p>
        </div>
      ) : (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-primary mb-2">
            <CheckIcon className="w-5 h-5" />
            <span className="font-medium">You've confirmed!</span>
          </div>
          <p className="text-sm text-cream/60">
            {otherPartyConfirmed
              ? 'Processing payout...'
              : `Waiting for the ${isWinner ? 'seller' : 'winner'} to confirm. Once both parties confirm, ${isSeller ? 'your payout will be processed' : 'the seller will receive their payout'}.`
            }
          </p>
        </div>
      )}

      {/* Help text */}
      <div className="mt-4 pt-4 border-t border-cream/10">
        <p className="text-xs text-cream/40">
          Having an issue? If the item doesn't match the description or there's a problem with collection, you can <button className="text-primary hover:underline">report a problem</button>.
        </p>
      </div>
    </div>
  );
}

/**
 * StatusItem - Individual status line item
 */
function StatusItem({ label, checked, isCurrentUser }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center transition-colors',
          checked
            ? 'bg-success text-white'
            : 'bg-midnight-lighter text-cream/30'
        )}
      >
        {checked ? (
          <CheckIcon className="w-4 h-4" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-current" />
        )}
      </div>
      <span className={cn(
        'text-sm',
        checked ? 'text-cream' : 'text-cream/50'
      )}>
        {label}
        {isCurrentUser && !checked && (
          <span className="text-primary ml-1">(You)</span>
        )}
        {isCurrentUser && checked && (
          <span className="text-success ml-1">(You)</span>
        )}
      </span>
    </div>
  );
}

/**
 * CompactHandoverStatus - Minimal status for card views
 */
export function CompactHandoverStatus({ handover, className }) {
  const { winnerConfirmed, sellerConfirmed, status } = handover || {};

  if (status === 'completed') {
    return (
      <div className={cn('flex items-center gap-2 text-success', className)}>
        <CheckIcon className="w-4 h-4" />
        <span className="text-sm font-medium">Handover complete</span>
      </div>
    );
  }

  if (status === 'disputed') {
    return (
      <div className={cn('flex items-center gap-2 text-error', className)}>
        <AlertIcon className="w-4 h-4" />
        <span className="text-sm font-medium">Disputed</span>
      </div>
    );
  }

  const confirmCount = (winnerConfirmed ? 1 : 0) + (sellerConfirmed ? 1 : 0);

  return (
    <div className={cn('flex items-center gap-2 text-cream/60', className)}>
      <ClockIcon className="w-4 h-4" />
      <span className="text-sm">{confirmCount}/2 confirmed</span>
    </div>
  );
}

// Icon components
function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function HandshakeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
